/**
 * Formats a Date object into a localized date string (MM/DD/YYYY).
 * 
 * Function is used within the formatDateString, todaysDateString, and getNextBusinessDayString functions.
 * 
 * - Uses the 'en-US' locale.
 * - Ensures two-digit month and day formatting.
 * 
 * @param date The Date object to format.
 * @returns A formatted date string in MM/DD/YYYY format.
 */
export function localDateStringFormat(date: Date): string {
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
}

/**
 * Converts a date string into a formatted date string (MM/DD/YYYY).
 * 
 * Function is used within the ReceivingLogComponent to format the date of the log.
 * 
 * - Parses the input string into a Date object.
 * - Uses `localDateStringFormat` to standardize the format.
 * - Returns a properly formatted date or an invalid date if the input is incorrect.
 * 
 * @param initialDate A date string to format.
 * @returns A formatted date string in MM/DD/YYYY format.
 */
export function formatDateString(initialDate:string): string {
    const newDate= new Date(initialDate);
    const formattedDate = localDateStringFormat(newDate);
    return formattedDate;
}


/**
 * Retrieves today's date as a formatted string (MM/DD/YYYY).
 * 
 * Function is used within the NewLog model to set and format the date of a new log.
 * 
 * - Uses the current system date.
 * - Formats it using `localDateStringFormat`.
 * 
 * @returns Today's date in MM/DD/YYYY format.
 */
export function todaysDateString(): string {
    const today = new Date();
    const todayFormatted = localDateStringFormat(today);
    return todayFormatted;
}



/**
 * Calculates the next business day (MM/DD/YYYY).
 * 
 * Function is used within the LogFormComponent to set the dateOut of a new log.
 * 
 * - Starts from the current date.
 * - If the next day is Saturday, moves to Monday.
 * - If the next day is Sunday, moves to Monday.
 * - Uses `localDateStringFormat` for consistent formatting.
 * 
 * @returns The next business day as a formatted string.
 */
export function getNextBusinessDayString():string {
    const today = new Date();
    let nextDay = new Date(today);
    
    // Add one day
    nextDay.setDate(today.getDate() + 1);
    
    // Check if the next day is a weekend
    const dayOfWeek = nextDay.getDay(); // 0 = Sunday, 6 = Saturday
    
    if (dayOfWeek === 6) { // Saturday -> add 2 days to make it Monday
        nextDay.setDate(nextDay.getDate() + 2);
    } else if (dayOfWeek === 0) { // Sunday -> add 1 day to make it Monday
        nextDay.setDate(nextDay.getDate() + 1);
    }

    const nextDayFormatted = localDateStringFormat(nextDay);
    
    return nextDayFormatted;
}


